import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Hook to manage guest tabs (ardoises) via Supabase.
 * Handles: tabs (one per guest per stay), tab_entries (each consumption),
 * and menu_items (the bar/kitchen presets used by the item picker).
 * Falls back to in-memory sample data if Supabase is not configured.
 */

const isConfigured = import.meta.env.VITE_SUPABASE_ANON_KEY &&
  import.meta.env.VITE_SUPABASE_ANON_KEY !== 'placeholder';

/* ─── Sample data (dev / demo mode only) ──────────────── */
const SAMPLE_MENU = [
  { id: 'm1', name: 'Coca-Cola', category: 'drinks', price: 2, active: true, sort_order: 4 },
  { id: 'm2', name: 'Heineken', category: 'drinks', price: 3, active: true, sort_order: 7 },
  { id: 'm3', name: 'Negroni', category: 'drinks', price: 8, active: true, sort_order: 11 },
  { id: 'm4', name: 'Vin Rouge Sauzet Saint-Bau', category: 'drinks', price: 15, active: true, sort_order: 17 },
  { id: 'm5', name: 'Houmous', category: 'food', price: 7, active: true, sort_order: 5 },
  { id: 'm6', name: 'Pinsa Romana Pizza', category: 'food', price: 6, active: true, sort_order: 3 },
  { id: 'm7', name: 'Fruit', category: 'food', price: 1, active: true, sort_order: 19 },
];

/* ─── Mappers: DB (snake) <-> frontend (camel) ────────── */
function tabToFrontend(row) {
  return {
    id: row.id,
    bookingId: row.booking_id,
    guestName: row.guest_name || '',
    guestEmail: row.guest_email || '',
    roomLabel: row.room_label || '',
    status: row.status || 'open',
    settledAt: row.settled_at || null,
    settledMethod: row.settled_method || null,
    settledTotal: row.settled_total != null ? Number(row.settled_total) : 0,
    createdAt: row.created_at || null,
  };
}

function tabToBackend(tab) {
  return {
    booking_id: tab.bookingId || null,
    guest_name: tab.guestName || '',
    guest_email: tab.guestEmail || '',
    room_label: tab.roomLabel || '',
    status: tab.status || 'open',
  };
}

function entryToFrontend(row) {
  return {
    id: row.id,
    tabId: row.tab_id,
    itemName: row.item_name,
    price: Number(row.price),
    qty: row.qty || 1,
    addedBy: row.added_by || 'host',
    createdAt: row.created_at || null,
  };
}

function menuToFrontend(row) {
  return {
    id: row.id,
    name: row.name,
    category: row.category,
    price: Number(row.price),
    active: row.active !== false,
    sortOrder: row.sort_order || 0,
  };
}

export function useTabs() {
  const [tabs, setTabs] = useState([]);
  const [entries, setEntries] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ─── Fetch everything ──────────────────────────────── */
  const fetchAll = useCallback(async () => {
    if (!isConfigured) {
      setMenuItems(SAMPLE_MENU.map(menuToFrontend));
      setTabs([]);
      setEntries([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const [tabsRes, entriesRes, menuRes] = await Promise.all([
        supabase.from('tabs').select('*').order('created_at', { ascending: true }),
        supabase.from('tab_entries').select('*').order('created_at', { ascending: true }),
        supabase.from('menu_items').select('*').eq('active', true).order('sort_order', { ascending: true }),
      ]);
      if (tabsRes.error) throw tabsRes.error;
      if (entriesRes.error) throw entriesRes.error;
      if (menuRes.error) throw menuRes.error;
      setTabs((tabsRes.data || []).map(tabToFrontend));
      setEntries((entriesRes.data || []).map(entryToFrontend));
      setMenuItems((menuRes.data || []).map(menuToFrontend));
      setError(null);
    } catch (e) {
      console.error('Fetch tabs error:', e);
      setError(e.message);
      setMenuItems(SAMPLE_MENU.map(menuToFrontend));
    } finally {
      setLoading(false);
    }
  }, []);

  /* ─── Create tab ────────────────────────────────────── */
  const createTab = useCallback(async (tab) => {
    if (!isConfigured) {
      const newTab = tabToFrontend({
        id: 't' + Date.now(),
        booking_id: tab.bookingId || null,
        guest_name: tab.guestName,
        guest_email: tab.guestEmail,
        room_label: tab.roomLabel,
        status: 'open',
        created_at: new Date().toISOString(),
      });
      setTabs(prev => [...prev, newTab]);
      return newTab;
    }
    const { data, error: err } = await supabase
      .from('tabs')
      .insert(tabToBackend({ ...tab, status: 'open' }))
      .select()
      .single();
    if (err) throw err;
    const ft = tabToFrontend(data);
    setTabs(prev => [...prev, ft]);
    return ft;
  }, []);

  /* ─── Delete tab (cascades to entries in DB) ─────────── */
  const deleteTab = useCallback(async (id) => {
    if (!isConfigured) {
      setTabs(prev => prev.filter(t => t.id !== id));
      setEntries(prev => prev.filter(e => e.tabId !== id));
      return;
    }
    const { error: err } = await supabase.from('tabs').delete().eq('id', id);
    if (err) throw err;
    setTabs(prev => prev.filter(t => t.id !== id));
    setEntries(prev => prev.filter(e => e.tabId !== id));
  }, []);

  /* ─── Add entry ─────────────────────────────────────── */
  const addEntry = useCallback(async ({ tabId, itemName, price, qty = 1 }) => {
    if (!isConfigured) {
      const newEntry = entryToFrontend({
        id: 'e' + Date.now(),
        tab_id: tabId,
        item_name: itemName,
        price,
        qty,
        added_by: 'admin',
        created_at: new Date().toISOString(),
      });
      setEntries(prev => [...prev, newEntry]);
      return newEntry;
    }
    const { data, error: err } = await supabase
      .from('tab_entries')
      .insert({ tab_id: tabId, item_name: itemName, price, qty, added_by: 'admin' })
      .select()
      .single();
    if (err) throw err;
    const fe = entryToFrontend(data);
    setEntries(prev => [...prev, fe]);
    return fe;
  }, []);

  /* ─── Delete entry ──────────────────────────────────── */
  const deleteEntry = useCallback(async (id) => {
    if (!isConfigured) {
      setEntries(prev => prev.filter(e => e.id !== id));
      return;
    }
    const { error: err } = await supabase.from('tab_entries').delete().eq('id', id);
    if (err) throw err;
    setEntries(prev => prev.filter(e => e.id !== id));
  }, []);

  /* ─── Update entry qty (qty <= 0 removes the line) ──── */
  const updateEntryQty = useCallback(async (id, qty) => {
    if (qty <= 0) {
      return deleteEntry(id);
    }
    if (!isConfigured) {
      setEntries(prev => prev.map(e => e.id === id ? { ...e, qty } : e));
      return;
    }
    const { data, error: err } = await supabase
      .from('tab_entries')
      .update({ qty })
      .eq('id', id)
      .select()
      .single();
    if (err) throw err;
    const fe = entryToFrontend(data);
    setEntries(prev => prev.map(e => e.id === fe.id ? fe : e));
    return fe;
  }, [deleteEntry]);

  /* ─── Settle tab ────────────────────────────────────── */
  const settleTab = useCallback(async (id, { method, total }) => {
    const settledAt = new Date().toISOString();
    if (!isConfigured) {
      setTabs(prev => prev.map(t => t.id === id
        ? { ...t, status: 'settled', settledMethod: method, settledTotal: total, settledAt }
        : t));
      return;
    }
    const { data, error: err } = await supabase
      .from('tabs')
      .update({ status: 'settled', settled_method: method, settled_total: total, settled_at: settledAt })
      .eq('id', id)
      .select()
      .single();
    if (err) throw err;
    const ft = tabToFrontend(data);
    setTabs(prev => prev.map(t => t.id === ft.id ? ft : t));
    return ft;
  }, []);

  /* ─── Reopen a settled tab (undo) ───────────────────── */
  const reopenTab = useCallback(async (id) => {
    if (!isConfigured) {
      setTabs(prev => prev.map(t => t.id === id
        ? { ...t, status: 'open', settledMethod: null, settledTotal: 0, settledAt: null }
        : t));
      return;
    }
    const { data, error: err } = await supabase
      .from('tabs')
      .update({ status: 'open', settled_method: null, settled_total: 0, settled_at: null })
      .eq('id', id)
      .select()
      .single();
    if (err) throw err;
    const ft = tabToFrontend(data);
    setTabs(prev => prev.map(t => t.id === ft.id ? ft : t));
    return ft;
  }, []);

  /* ─── Helpers ───────────────────────────────────────── */
  const entriesForTab = useCallback(
    (tabId) => entries.filter(e => e.tabId === tabId),
    [entries]
  );
  const tabTotal = useCallback(
    (tabId) => entries
      .filter(e => e.tabId === tabId)
      .reduce((sum, e) => sum + e.price * e.qty, 0),
    [entries]
  );
  const tabsForBooking = useCallback(
    (bookingId) => tabs.filter(t => t.bookingId === bookingId),
    [tabs]
  );

  /* ─── Realtime ──────────────────────────────────────── */
  useEffect(() => {
    fetchAll();
    if (!isConfigured) return;
    const channel = supabase
      .channel('tabs-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tabs' }, () => fetchAll())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'tab_entries' }, () => fetchAll())
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchAll]);

  return {
    tabs,
    entries,
    menuItems,
    loading,
    error,
    createTab,
    deleteTab,
    addEntry,
    deleteEntry,
    updateEntryQty,
    settleTab,
    reopenTab,
    entriesForTab,
    tabTotal,
    tabsForBooking,
    refetch: fetchAll,
  };
}
